import axios from 'axios';

const BASE_URL = process.env.REACT_APP_API_URL;

export interface Employee {
  employeeId: number;
  employeeName: string;
  position: string | null;
  subcontractor: boolean;
  sortOrder: number | null;
  sageDepartment: string | null;
  adminRole: boolean;
  email: string | null;
  worktypeDefaultId: number | null;
  worktypeDefault: any | null;
  worktypegroupDefaultId: number | null;
  worktypegroupDefault: any | null;
  hourlyRate: number | null;
  mobilePhone: string;
  vehicleRegistration: string | null;
  permissions: any | null;
  fcmTokens: any[];
  type: any | null;
}

export interface Notification {
  notificationId: number;
  recipientEmployeeId: number | null;
  recipientGroupId: number | null;
  subject: string | null;
  message: string;
  isRead: boolean;
  createdDate: string;
  employeeCreatedbyId: number;
  modifiedDate: string | null;
  employeeModifiedbyId: number | null;
  deletedDate: string | null;
  employeeDeletedbyId: number | null;
  formId: number | null;
  recipientEmployee: Employee | null;
  employeeCreatedby: Employee;
  employeeModifiedby: Employee | null;
  employeeDeletedby: Employee | null;
}

export interface CreateNotificationPayload {
  recipientEmployeeId?: number | null;
  recipientGroupId?: number | null;
  subject?: string;
  message: string;
  fromPortal: boolean;
  formId?: number | null;
  mandatoryRead?: boolean;
}

export interface SendNotificationPayload {
  /** One notification is created for each employee. */
  recipientEmployeeIds: number[];
  /** One notification is created for each group; someone in several is notified once. */
  recipientGroupIds: number[];
  subject: string;
  message: string;
  formId: number | null;
  mandatoryRead: boolean;
}

/**
 * Sends a notification with any attached files in a single request. The API
 * creates the notification(s), stores the files once and only then sends the
 * push, so recipients never open a notification before its files are there.
 */
export const sendNotification = async (
  payload: SendNotificationPayload,
  attachments: File[]
): Promise<Notification[]> => {
  const token = localStorage.getItem('authToken');
  const formData = new FormData();
  formData.append('payload', JSON.stringify(payload));
  attachments.forEach((file) => formData.append('files', file, file.name));

  const response = await axios.post(`https://${BASE_URL}/notification/send`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Get all notifications
 */
export const getNotifications = async (): Promise<Notification[]> => {
  const token = localStorage.getItem('authToken');
  const response = await axios.get(`https://${BASE_URL}/notification/grid`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

/**
 * Create a new notification
 */
export const createNotification = async (data: CreateNotificationPayload): Promise<Notification> => {
  const token = localStorage.getItem('authToken');
  const response = await axios.post(`https://${BASE_URL}/notification`, data, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};

export interface NotificationGroupStatus {
  notificationGroupStatusId: number;
  groupId: number;
  notificationId: number;
  employeeId: number;
  isRead: boolean;
  employee: Employee;
}

/**
 * Get notification group status for a specific notification
 */
export const getNotificationGroupStatus = async (notificationId: number): Promise<NotificationGroupStatus[]> => {
  const token = localStorage.getItem('authToken');
  const response = await axios.get(`https://${BASE_URL}/notificationgroupstatus/notification/${notificationId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response.data;
};
